/**
 * Environment Configuration Utility
 *
 * Provides environment-aware URLs and configuration for different deployment targets:
 * - Local: Firebase emulators
 * - Staging: wisetree-ai-planner-staging project
 * - Production: wisetree-ai-planner project
 */

/**
 * Whitelist of allowed API domains for security
 * Prevents accidental or malicious API redirection
 */
const ALLOWED_API_DOMAINS = [
  'us-central1-wisetree-ai-planner.cloudfunctions.net',
  'us-central1-wisetree-ai-planner-staging.cloudfunctions.net',
  'localhost',
  '127.0.0.1',
  'api.wisetree.ai' // Custom API domain if configured
];

/**
 * Validate that a URL points to an allowed API domain
 * @param url - The URL to validate
 * @returns true if valid, false otherwise
 */
function isAllowedApiDomain(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check if hostname matches any allowed domain
    return ALLOWED_API_DOMAINS.some(domain =>
      hostname === domain.toLowerCase() ||
      hostname.endsWith(`.${domain.toLowerCase()}`)
    );
  } catch (error) {
    console.error('Invalid URL format:', url);
    return false;
  }
}

/**
 * Get the base URL for Cloud Functions API
 *
 * Priority:
 * 1. REACT_APP_API_BASE_URL (explicit override)
 * 2. REACT_APP_FUNCTIONS_EMULATOR_URL (local development)
 * 3. Auto-detect from REACT_APP_ENV or Firebase project ID
 *
 * @throws {Error} If REACT_APP_API_BASE_URL is set but empty
 */
export function getApiBaseUrl(): string {
  let url: string;

  // Explicit API base URL override
  if (process.env.REACT_APP_API_BASE_URL) {
    url = process.env.REACT_APP_API_BASE_URL.trim();
    if (!url) {
      throw new Error('REACT_APP_API_BASE_URL is set but empty');
    }
  }
  // Local development with emulators
  else if (process.env.REACT_APP_FUNCTIONS_EMULATOR_URL) {
    url = process.env.REACT_APP_FUNCTIONS_EMULATOR_URL.trim();
    if (!url) {
      console.warn('REACT_APP_FUNCTIONS_EMULATOR_URL is empty, falling back to auto-detection');
      url = getDefaultApiUrl();
    }
  }
  // Auto-detect from environment or project ID
  else {
    url = getDefaultApiUrl();
  }

  // Remove trailing slash for consistency
  url = url.replace(/\/$/, '');

  // Validate domain for security
  if (!isAllowedApiDomain(url)) {
    console.error(`Security Warning: API URL "${url}" is not in the allowed domains list.`);
    console.error('Allowed domains:', ALLOWED_API_DOMAINS);
    console.error('Falling back to production URL for safety.');
    return 'https://us-central1-wisetree-ai-planner.cloudfunctions.net';
  }

  // Log environment info for debugging (non-production only)
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Environment] API Base URL: ${url}`);
    console.log(`[Environment] Detected Environment: ${getEnvironment()}`);
  }

  return url;
}

/**
 * Get the default API URL based on environment auto-detection
 * @private
 */
function getDefaultApiUrl(): string {
  const env = (process.env.REACT_APP_ENV || 'production').toLowerCase().trim();
  const projectId = (process.env.REACT_APP_FIREBASE_PROJECT_ID || 'wisetree-ai-planner').trim();

  // Staging environment
  if (env === 'staging' || projectId === 'wisetree-ai-planner-staging') {
    return 'https://us-central1-wisetree-ai-planner-staging.cloudfunctions.net';
  }

  // Production environment (default)
  return 'https://us-central1-wisetree-ai-planner.cloudfunctions.net';
}

/**
 * Get the current environment name
 */
export function getEnvironment(): 'local' | 'staging' | 'production' {
  // Local dev: the CRA dev server (`npm start`) sets NODE_ENV=development, and we
  // commonly point .env.local at the staging Firebase project. Treat that as
  // local — so the prod landing shows (no staging login blocker) and the staging
  // allowlist is skipped. Staging/prod builds run NODE_ENV=production, unaffected.
  if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_USE_EMULATORS === 'true') {
    return 'local';
  }

  // Check for emulator URL (even if empty, the presence indicates local intent)
  if (process.env.REACT_APP_FUNCTIONS_EMULATOR_URL) {
    const url = process.env.REACT_APP_FUNCTIONS_EMULATOR_URL.trim();
    if (url) {
      return 'local';
    }
  }

  const env = (process.env.REACT_APP_ENV || 'production').toLowerCase().trim();
  const projectId = (process.env.REACT_APP_FIREBASE_PROJECT_ID || 'wisetree-ai-planner').trim();

  if (env === 'staging' || projectId === 'wisetree-ai-planner-staging') {
    return 'staging';
  }

  // Default to production for safety
  return 'production';
}

/**
 * Check if running in local development
 */
export function isLocalDevelopment(): boolean {
  return getEnvironment() === 'local';
}

/**
 * Check if running in staging
 */
export function isStaging(): boolean {
  return getEnvironment() === 'staging';
}

/**
 * Check if running in production
 */
export function isProduction(): boolean {
  return getEnvironment() === 'production';
}

/**
 * Get the Firebase Hosting URL for the current environment
 */
export function getHostingUrl(): string {
  const env = getEnvironment();

  if (env === 'local') {
    return 'http://localhost:5173';
  }

  if (env === 'staging') {
    return 'https://staging.wisetree.ai';
  }

  return 'https://wisetree.ai';
}

/**
 * Get build information for debugging and monitoring
 */
export function getBuildInfo() {
  const buildTime = process.env.REACT_APP_BUILD_TIME || 'unknown';
  const buildVersion = process.env.REACT_APP_VERSION || '1.0.1';
  const gitCommit = process.env.REACT_APP_GIT_COMMIT || 'unknown';

  return {
    version: buildVersion,
    buildTime,
    gitCommit: gitCommit.substring(0, 7), // Short commit hash
    environment: getEnvironment(),
    apiBaseUrl: getApiBaseUrl(),
    nodeEnv: process.env.NODE_ENV
  };
}

/**
 * Log build information to console (development mode only)
 * Safe to call multiple times - only logs once
 */
let buildInfoLogged = false;
export function logBuildInfo() {
  if (buildInfoLogged || process.env.NODE_ENV !== 'development') {
    return;
  }

  buildInfoLogged = true;
  const info = getBuildInfo();

  console.log('%c🚀 WiseTree AI Planner', 'font-size: 16px; font-weight: bold; color: #4F46E5;');
  console.log(`%cVersion: ${info.version}`, 'color: #6B7280;');
  console.log(`%cEnvironment: ${info.environment}`, 'color: #6B7280;');
  console.log(`%cAPI: ${info.apiBaseUrl}`, 'color: #6B7280;');
  console.log(`%cBuild: ${info.buildTime}`, 'color: #6B7280;');
  console.log(`%cCommit: ${info.gitCommit}`, 'color: #6B7280;');
}
